import React, {
  useEffect,
  useState,
} from 'react';
import {
  Outlet,
  useNavigate,
} from 'react-router-dom';
import FullScreenSpinner from 'core/components/FullScreenSpinner';
import {
  useAuth,
} from 'core/hooks';
import {
  APP_ROUTES,
} from 'views/EMR/router';

const SUPPORT_SILENT_AUTH_KEY = 'support_silent_auth_attempted_v1';

export default function SupportProtectedLayout() {
  const navigate = useNavigate();
  const {
    isAuthenticated,
    isLoading,
    issueAccessToken,
    loginWithRedirect,
  } = useAuth();
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    if (isLoading) return undefined;

    if (!isAuthenticated) {
      setIsReady(false);

      // If user refreshes /support/portal, Auth0 may lose in-memory state.
      // Try a one-time silent auth redirect (prompt=none). If there is an Auth0 session,
      // it will bounce back authenticated. Otherwise, /authorize will get login_required
      // and we will redirect to /support.
      const attempted = sessionStorage.getItem(SUPPORT_SILENT_AUTH_KEY) === '1';
      if (!attempted) {
        sessionStorage.setItem(SUPPORT_SILENT_AUTH_KEY, '1');
        const targetUrl = `${window.location.pathname}${window.location.search}`;
        loginWithRedirect({
          authorizationParams: {
            prompt: 'none',
          },
          appState: { targetUrl },
        });
        return undefined;
      }

      navigate(APP_ROUTES.POPULATE_SUPPORT.ROOT, { replace: true });
      return undefined;
    }

    sessionStorage.removeItem(SUPPORT_SILENT_AUTH_KEY);

    let cancelled = false;
    (async () => {
      setIsReady(false);
      try {
        await issueAccessToken();
      } finally {
        if (!cancelled) setIsReady(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, isLoading, issueAccessToken, loginWithRedirect, navigate]);

  return isLoading || !isAuthenticated || !isReady ? <FullScreenSpinner /> : <Outlet />;
}
